import React, { useMemo } from 'react';
import ContentStackImage from '@/components/customercenter-ui/common/contentStackImage/contentStackImage';
import useMediaQuery from '@/frontastic/hooks/useMediaQuery';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import {
  buildCmsImageUrl,
  cdnWidthForBadgeLayout,
  mergeImageSizesImageBoxStyle,
  pickBadgeImageSizeRow,
  resolveBadgeImageLayoutFromRow,
} from '@/helpers/utils/badgeImageSizes';
import type { BadgeConfiguration } from '@/helpers/utils/productBadging';
import { desktop } from '@/helpers/utils/screensizes';

/** Matches stoneberry PLP/PDP pill baseline for CMS rule badges. */
const baseBadgeSpanClassName =
  'mr-1 inline-flex min-h-[30px] items-center justify-center rounded-[4px] p-1 px-[6px] text-center text-[10px] font-semibold leading-7 text-white md:px-3 md:text-[14px]';

export interface ProductRuleBadgesProps {
  badges: BadgeConfiguration[];
  /** `pdpCorner`: absolute corners from `position`; `inline`: row (e.g. PLP). */
  layout?: 'pdpCorner' | 'inline';
}

const isRuleBadgeEnabled = (enabled: unknown): boolean => {
  if (enabled === false || enabled === 0) return false;
  if (typeof enabled === 'string') {
    const normalized = enabled.trim().toLowerCase();
    return normalized !== 'false' && normalized !== '0';
  }
  return true;
};

/** Image-only badges often omit `badgeName`; text badges need a label or they would render empty. */
function hasRenderableRuleBadgeContent(entry: BadgeConfiguration): boolean {
  if (entry.badgeName != null && String(entry.badgeName).trim().length > 0) return true;
  if (entry.backgroundImage?.trim()) return true;
  return false;
}

const norm = (v: unknown) =>
  String(v ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s/g, '');

type Corner = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

function firstNonEmptyString(...candidates: (string | undefined | null)[]): string | undefined {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
  }
  return undefined;
}

function normalizeCorner(position?: string): Corner {
  const p = norm(position);
  if (p.includes('bottom') && p.includes('right')) return 'bottomRight';
  if (p.includes('bottom') && p.includes('left')) return 'bottomLeft';
  if (p.includes('top') && p.includes('right')) return 'topRight';
  return 'topLeft';
}

function cornerWrapperClass(corner: Corner): string {
  switch (corner) {
    case 'topRight':
      return 'pointer-events-auto absolute right-1 top-1 z-[12] flex flex-row flex-wrap justify-end gap-1';
    case 'bottomLeft':
      return 'pointer-events-auto absolute bottom-1 left-1 z-[12] flex flex-row flex-wrap items-center gap-1';
    case 'bottomRight':
      return 'pointer-events-auto absolute bottom-1 right-1 z-[12] flex flex-row flex-wrap justify-end gap-1';
    default:
      return 'pointer-events-auto absolute left-1 top-1 z-[12] flex flex-row flex-wrap items-center gap-1';
  }
}

function resolvedShapeNorm(entry: BadgeConfiguration): string {
  return norm(firstNonEmptyString(entry.shape, entry.Shape, entry.shape_type));
}

function isCircleShape(entry: BadgeConfiguration): boolean {
  const s = resolvedShapeNorm(entry);
  return s === 'circle' || s === 'round' || s === 'circular';
}

function shapeBorderRadiusStyle(entry: BadgeConfiguration): React.CSSProperties {
  const s = resolvedShapeNorm(entry);
  if (s === 'circle' || s === 'round' || s === 'circular') {
    return { borderRadius: 9999 };
  }
  return { borderRadius: 4 };
}

function shapeClassFromResolved(entry: BadgeConfiguration): string {
  if (isCircleShape(entry)) {
    return 'rounded-full h-[56px] w-[56px] min-h-[56px] min-w-[56px] p-0 px-0 leading-tight';
  }
  return 'rounded-[4px]';
}

function ruleBadgeBaseClassName(): string {
  return baseBadgeSpanClassName
    .replace(/^mr-1\s/, '')
    .replace(/\brounded-\[4px]\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function pillShellClass(entry: BadgeConfiguration): string {
  return `${ruleBadgeBaseClassName()} ${shapeClassFromResolved(entry)}`.trim();
}

function imageBadgeShellClass(entry: BadgeConfiguration): string {
  if (isCircleShape(entry)) {
    return pillShellClass(entry);
  }
  return `${pillShellClass(entry)} w-[72px] sm:w-[84px] md:w-[96px] lg:w-[108px] min-h-0 p-0 px-0 leading-none`.trim();
}

function RuleBadgePill({
  entry,
  loadPriority,
  extraClass,
}: {
  entry: BadgeConfiguration;
  loadPriority: boolean;
  extraClass?: string;
}) {
  const [, innerWidth] = useMediaQuery(desktop);
  const projectSettings = useProjSettings();
  const rawSrc = entry.backgroundImage?.trim();
  const src = rawSrc || '';
  const textColor =
    entry.badgeTextColor != null && String(entry.badgeTextColor).trim() !== '' ? { color: entry.badgeTextColor } : {};

  const imgLayout = useMemo(() => {
    const row = pickBadgeImageSizeRow(entry.imageSizes, innerWidth);
    return resolveBadgeImageLayoutFromRow(row);
  }, [entry.imageSizes, innerWidth]);

  const hasImageSizeLayout = Boolean(src) && imgLayout.kind !== 'none';

  const imageBoxStyle = useMemo(() => {
    if (!src) return {};
    return mergeImageSizesImageBoxStyle(imgLayout, isCircleShape(entry));
  }, [src, imgLayout, entry]);

  const imgUrl = useMemo(
    () => (src ? buildCmsImageUrl(src, cdnWidthForBadgeLayout(imgLayout), projectSettings) : ''),
    [src, imgLayout, projectSettings?.imageHost, projectSettings?.webpEnabled],
  );

  const imageOuterClass = hasImageSizeLayout
    ? `relative isolate overflow-hidden inline-block shrink-0 ${ruleBadgeBaseClassName()} ${
        isCircleShape(entry) ? 'rounded-full p-0 px-0 leading-none' : 'rounded-[4px] p-0 px-0 leading-none min-h-0'
      } ${extraClass ?? ''}`.trim()
    : `relative isolate overflow-hidden ${imageBadgeShellClass(entry)} ${extraClass ?? ''}`.trim();

  const ruleImgClass =
    hasImageSizeLayout && imgLayout.kind === 'widthLead' && !isCircleShape(entry)
      ? 'block h-auto w-full max-w-full object-contain'
      : hasImageSizeLayout && imgLayout.kind === 'heightLead' && !isCircleShape(entry)
      ? 'block h-full w-auto max-h-full object-contain'
      : hasImageSizeLayout
      ? 'block h-full w-full object-contain'
      : '';

  if (src) {
    return (
      <span className={imageOuterClass} style={{ ...shapeBorderRadiusStyle(entry), ...textColor }}>
        {hasImageSizeLayout ? (
          <span style={imageBoxStyle} className="relative block shrink-0">
            {/* eslint-disable-next-line @next/next/no-img-element -- `imageSizes` uses intrinsic aspect; Next/Image ratio would not match */}
            <img
              src={imgUrl}
              alt={String(entry.badgeName ?? entry.badgeKey ?? 'Product badge')}
              className={ruleImgClass}
              loading={loadPriority ? 'eager' : 'lazy'}
              decoding="async"
            />
          </span>
        ) : (
          <ContentStackImage
            className="h-auto w-full object-contain"
            src={src}
            imageHeight={690}
            imageWidth={690}
            alt={String(entry.badgeName ?? entry.badgeKey ?? 'Product badge')}
            fullWidth={true}
            loadPriority={loadPriority}
          />
        )}
        <span className="pointer-events-none absolute inset-0 z-[1] flex w-full items-center justify-center px-1 text-center drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)]">
          {entry.badgeName}
        </span>
      </span>
    );
  }

  return (
    <span
      className={`${pillShellClass(entry)} ${extraClass ?? ''}`.trim()}
      style={{
        ...shapeBorderRadiusStyle(entry),
        ...textColor,
        ...(entry.badgeColor ? { backgroundColor: entry.badgeColor } : {}),
      }}
    >
      {entry.badgeName}
    </span>
  );
}

const ProductRuleBadges: React.FC<ProductRuleBadgesProps> = ({ badges, layout = 'inline' }) => {
  const visible = useMemo(
    () =>
      Array.isArray(badges)
        ? badges.filter((entry) => hasRenderableRuleBadgeContent(entry) && isRuleBadgeEnabled(entry.Enabled))
        : [],
    [badges],
  );

  if (!visible.length) return null;

  if (layout === 'inline') {
    let inlinePriorityAssigned = false;
    return (
      <span className="flex flex-wrap items-center gap-1">
        {visible.map((entry) => {
          const hasBg = Boolean(entry.backgroundImage?.trim());
          const loadPriority = hasBg && !inlinePriorityAssigned;
          if (loadPriority) inlinePriorityAssigned = true;
          return (
            <span key={entry.badgeKey ?? entry.badgeName} className="mr-1">
              <RuleBadgePill entry={entry} loadPriority={loadPriority} />
            </span>
          );
        })}
      </span>
    );
  }

  const byCorner: Record<Corner, BadgeConfiguration[]> = {
    topLeft: [],
    topRight: [],
    bottomLeft: [],
    bottomRight: [],
  };
  visible.forEach((entry) => {
    byCorner[normalizeCorner(firstNonEmptyString(entry.position, entry.Position))].push(entry);
  });

  const cornerOrder: Corner[] = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'];
  let priorityImageAssigned = false;

  return (
    <>
      {cornerOrder.map((corner) => {
        const list = byCorner[corner];
        if (!list.length) return null;
        return (
          <div key={corner} className={cornerWrapperClass(corner)}>
            {list.map((entry) => {
              const hasBg = Boolean(entry.backgroundImage?.trim());
              const loadPriority = hasBg && !priorityImageAssigned;
              if (loadPriority) priorityImageAssigned = true;
              return (
                <RuleBadgePill key={entry.badgeKey ?? entry.badgeName} entry={entry} loadPriority={loadPriority} />
              );
            })}
          </div>
        );
      })}
    </>
  );
};

export default ProductRuleBadges;
